import React, {useState} from 'react';
import {PaymentElement, useElements, useStripe} from '@stripe/react-stripe-js';
import {Actions_, Builder_, Dropdown_, Focus_, Grey_, Input_, Main_Text_Loader, countries} from 'monica-alexandria';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {checkVAT, countries as vatCountries} from 'jsvat';
import store from '../../../redux/store';
import {create_sidequest_subscription} from '../../../services/api/Subscriptions';
import {CheckoutExtraInput} from '../../../styles/Layout.css';

const vatCountryCodes = require('../../../config/vatCountryCodes').vatCountryCodes;

export default function Sidequest_Payment(props) {

    const {t} = useTranslation();
    const navigate = useNavigate();
    const stripe = useStripe();
    const elements = useElements();
    const user = store.getState().user.user;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [vatInput, setVatInput] = useState('');
    const [vatMessages, setVatMessages] = useState({errors: [], helpers: [], successes: []});

    const validate_vat = value => {

        const countryCode = vatCountryCodes[props.selectedCountry];
        const normalizedValue = value.trim().toUpperCase();
        const fullVatNumber = normalizedValue.startsWith(countryCode) ? normalizedValue : `${countryCode}${normalizedValue}`;
        return {result: checkVAT(fullVatNumber, vatCountries), fullVatNumber};
    };

    const check_vat = value => {

        setVatInput(value);
        const validation = validate_vat(value);
        if (validation.result?.isValid) {
            setVatMessages({errors: [], helpers: [], successes: [t('TIN is valid')]});
            return;
        }
        setVatMessages({errors: [], helpers: [t('TIN is not valid')], successes: []});
    };

    const submit_payment = async event => {

        event.preventDefault();
        if (!stripe || !elements || loading) return;

        const vatValidation = validate_vat(vatInput);
        if (!vatValidation.result?.isValid) {
            setVatMessages({errors: [t('TIN is not valid')], helpers: [], successes: []});
            return;
        }

        setLoading(true);
        setError(false);

        try {
            const submittedElements = await elements.submit();
            if (submittedElements.error) {
                setError(submittedElements.error.message);
                setLoading(false);
                return;
            }

            const paymentMethodResult = await stripe.createPaymentMethod({
                elements,
                params: {
                    billing_details: {
                        email: user?.username,
                        name: `${user?.firstname || ''} ${user?.lastname || ''}`.trim(),
                        address: {
                            country: props.selectedCountry,
                            postal_code: '',
                            state: '',
                            city: '',
                            line1: '',
                            line2: ''
                        }
                    }
                }
            });

            if (paymentMethodResult.error) {
                setError(paymentMethodResult.error.message);
                setLoading(false);
                return;
            }

            const response = await create_sidequest_subscription(
                props.session,
                paymentMethodResult.paymentMethod.id,
                vatValidation.fullVatNumber
            );
            if (response?.code !== 'SQ2000') {
                setError(response?.msg || t('Payment could not be completed. Please try again.'));
                setLoading(false);
                return;
            }

            if (response?.data?.clientSecret && !['succeeded', 'processing'].includes(response?.data?.paymentIntentStatus)) {
                const confirmation = await stripe.confirmPayment({
                    clientSecret: response.data.clientSecret,
                    confirmParams: {return_url: response.data.returnUrl},
                    redirect: 'if_required'
                });

                if (confirmation.error) {
                    setError(confirmation.error.message);
                    setLoading(false);
                    return;
                }
            }

            window.location.assign(response.data.returnUrl);
        } catch (requestError) {
            setError(requestError?.response?.data?.msg || requestError?.message || t('Payment could not be completed. Please try again.'));
            setLoading(false);
        }
    };

    return (
        <div>
            {loading ? (
                <Main_Text_Loader text={t('We are proccesing your information. Please do not leave the page.')}></Main_Text_Loader>
            ) : null}
            <CheckoutExtraInput>
                <Builder_ title={t('Country')}>
                    <Dropdown_>
                        <select value={props.selectedCountry} onChange={event => {
                            props.setSelectedCountry(event.target.value);
                            setVatInput('');
                            setVatMessages({errors: [], helpers: [], successes: []});
                        }}>
                            {countries.map(country => (
                                <option key={country.code} value={country.code}>{country.flag} {country.country}</option>
                            ))}
                        </select>
                    </Dropdown_>
                </Builder_>
                <Builder_>
                    <Input_
                        placeholder={t('Taxpayer Identification Number (TIN)')}
                        value={vatInput}
                        onChange={event => check_vat(event.target.value)}
                        errors={vatMessages.errors}
                        helpers={vatMessages.helpers}
                        successes={vatMessages.successes}
                    ></Input_>
                </Builder_>
            </CheckoutExtraInput>
            <PaymentElement options={{
                defaultValues: {billingDetails: {email: user?.username, address: {country: props.selectedCountry}}},
                fields: {name: 'never', email: 'never', billingDetails: {address: 'never'}},
                layout: {type: 'tabs', defaultCollapsed: false}
            }}></PaymentElement>
            {error ? (
                <p role="alert">{error}</p>
            ) : null}
            <Actions_>
                <Grey_ size="large" text={t('Back')} iconLeft="Back" onClick={() => navigate(-1)}></Grey_>
                <Focus_
                    size="large"
                    text={t('Pay now')}
                    disabled={!stripe || loading}
                    iconRight="Rocket"
                    onClick={submit_payment}
                ></Focus_>
            </Actions_>
        </div>
    );
}
